import { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Droplets, Box, Package, Layers, Warehouse, X, Calendar, Scale, Truck, ArrowRightLeft, LogOut, MoveRight, Hash, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper per ottenere data/ora italiana
const getItalianDateTime = () => {
  const now = new Date();
  const italianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = italianTime.getFullYear();
  const month = String(italianTime.getMonth() + 1).padStart(2, '0');
  const day = String(italianTime.getDate()).padStart(2, '0');
  const hours = String(italianTime.getHours()).padStart(2, '0');
  const minutes = String(italianTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const OperatoreStatoDocce = () => {
  const [stats, setStats] = useState(null);
  const [cellaStats, setCellaStats] = useState({ bins_attivi: 0, kg_totale: 0, bins: [] });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedBin, setSelectedBin] = useState(null);
  const [binMovimenti, setBinMovimenti] = useState([]);
  const [expandedId, setExpandedId] = useState(null); // ID espanso
  
  // Stati per le azioni del modal
  const [modalAction, setModalAction] = useState(null); // 'uscita', 'spostamento', 'unione'
  const [actionLoading, setActionLoading] = useState(false);
  const [binsStessoLotto, setBinsStessoLotto] = useState([]); // Per unione
  
  // Form data per uscita
  const [uscitaData, setUscitaData] = useState({
    kg_uscita: '',
    destinazione: '',
    data_ora_uscita: getItalianDateTime()
  });
  
  // Form data per spostamento
  const [spostamentoData, setSpostamentoData] = useState({
    kg_uscita: '',
    nuovo_bin_number: '',
    nuova_doccia_number: '',
    data_ora_uscita: getItalianDateTime()
  });

  // Form data per unione
  const [unioneData, setUnioneData] = useState({
    bin_destinazione: '',
    kg_da_trasferire: '',
    data_operazione: getItalianDateTime()
  });

  useEffect(() => {
    fetchData();
    fetchCellaStats();
    // Auto refresh ogni 30 secondi
    const interval = setInterval(() => {
      fetchData();
      fetchCellaStats();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      const now = new Date();
      setLastUpdate(now.toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome' }));
    } catch (error) {
      toast.error('Errore nel caricamento stato docce');
    } finally {
      setLoading(false);
    }
  };

  const fetchCellaStats = async () => {
    try {
      const response = await axios.get(`${API}/cella/stats`);
      setCellaStats(response.data);
    } catch (error) {
      console.error('Error fetching cella stats:', error);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchData();
    fetchCellaStats();
    toast.success('Stato aggiornato');
  };

  const handleOpenBinDetails = async (bin, docciaNum) => {
    try {
      const response = await axios.get(`${API}/bins/${bin.bin_number}/movimenti`);
      setBinMovimenti(response.data);
      setSelectedBin({ ...bin, doccia_number: docciaNum });
      setModalAction(null);
      // Reset form uscita
      setUscitaData({
        kg_uscita: '',
        destinazione: '',
        data_ora_uscita: getItalianDateTime()
      });
      // Reset form spostamento
      setSpostamentoData({
        kg_uscita: bin.kg || 0,
        nuovo_bin_number: '',
        nuova_doccia_number: '',
        data_ora_uscita: getItalianDateTime()
      });
      // Reset form unione
      setUnioneData({
        bin_destinazione: '',
        kg_da_trasferire: bin.kg || 0,
        data_operazione: getItalianDateTime()
      });
      setBinsStessoLotto([]);
    } catch (error) {
      console.error('Error fetching bin movimenti:', error);
      setBinMovimenti([]);
      setSelectedBin({ ...bin, doccia_number: docciaNum });
    }
  };

  const closeBinDetails = () => {
    setSelectedBin(null);
    setBinMovimenti([]);
    setModalAction(null);
    setBinsStessoLotto([]);
  };

  // Carica bins stesso lotto per unione
  const loadBinsStessoLotto = async (binNumber) => {
    try {
      const response = await axios.get(`${API}/bins/${binNumber}/bins-stesso-lotto`);
      setBinsStessoLotto(response.data.bins_disponibili || []);
    } catch (error) {
      console.error('Error loading bins stesso lotto:', error);
      setBinsStessoLotto([]);
    }
  };

  // Handler per Uscita Parziale
  const handleSubmitUscita = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        bin_number: selectedBin.bin_number,
        kg_uscita: parseFloat(uscitaData.kg_uscita),
        destinazione: uscitaData.destinazione,
        data_ora_uscita: new Date(uscitaData.data_ora_uscita).toISOString()
      };
      const response = await axios.post(`${API}/bins/uscita-parziale`, payload);
      toast.success(`Uscita registrata! Residuo: ${response.data.kg_residuo} kg`);
      fetchData();
      closeBinDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante uscita');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler per Spostamento
  const handleSubmitSpostamento = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        bin_number: selectedBin.bin_number,
        kg_uscita: parseFloat(spostamentoData.kg_uscita),
        nuovo_bin_number: spostamentoData.nuovo_bin_number,
        nuova_doccia_number: parseInt(spostamentoData.nuova_doccia_number),
        data_ora_uscita: new Date(spostamentoData.data_ora_uscita).toISOString()
      };
      await axios.post(`${API}/bins/uscita-parziale`, payload);
      toast.success('Spostamento completato!');
      fetchData();
      closeBinDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante spostamento');
    } finally {
      setActionLoading(false);
    }
  };

  // Handler per Unione Bins
  const handleSubmitUnione = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        bin_origine: selectedBin.bin_number,
        bin_destinazione: unioneData.bin_destinazione,
        kg_da_trasferire: parseFloat(unioneData.kg_da_trasferire),
        data_operazione: new Date(unioneData.data_operazione).toISOString()
      };
      const response = await axios.post(`${API}/bins/unisci`, payload);
      toast.success(response.data.message);
      fetchData();
      closeBinDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante unione');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '-';
    }
  };

  if (loading && !stats) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#FFE500] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const docceStato = stats?.docce_stato || {};
  
  // Filtra doccia 6 (inutilizzabile)
  const docceFiltered = Object.entries(docceStato).filter(([num]) => num !== '6');

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="operatore-stato-docce">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-[#FFE500] p-2 rounded-xl">
            <Droplets className="w-5 h-5 text-[#0A1628]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white uppercase tracking-tight">Stato Docce</h2>
            <p className="text-xs text-white/50">
              {lastUpdate && `Ultimo aggiornamento: ${lastUpdate}`}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white border border-white/20 rounded-xl text-xs font-bold uppercase hover:bg-white/20 transition-colors disabled:opacity-50"
          data-testid="refresh-stato-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </button>
      </div>

      {/* Riepilogo KPI */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-[#FFE500]">{stats?.bins_totali_usati || 0}</p>
          <p className="text-[10px] text-white/50 uppercase">Bins Attivi</p>
        </div>
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-emerald-400">{stats?.kg_in_depurazione || 0}</p>
          <p className="text-[10px] text-white/50 uppercase">KG Totali</p>
        </div>
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-blue-400">{stats?.kg_usciti_oggi || 0}</p>
          <p className="text-[10px] text-white/50 uppercase">KG Usciti Oggi</p>
        </div>
      </div>

      {/* Legenda */}
      <div className="bg-white/5 rounded-lg p-2 text-center">
        <p className="text-[10px] text-white/40">
          <span className="text-emerald-400">Verde</span> = &lt;3 bins | 
          <span className="text-amber-400 ml-2">Giallo</span> = 3 bins | 
          <span className="text-red-400 ml-2">Rosso</span> = 4 bins (piena)
        </p>
      </div>

      {/* Griglia Docce */}
      <div className="grid grid-cols-2 gap-3" data-testid="docce-grid">
        {docceFiltered.map(([docciaNum, doccia]) => {
          const hasBins = doccia.numero_bins > 0;
          const sortedBins = [...(doccia.bins || [])].sort((a, b) => (b.posizione || 1) - (a.posizione || 1));
          
          return (
            <div
              key={docciaNum}
              className={`rounded-xl p-3 transition-all ${
                hasBins 
                  ? 'bg-white/10 border-2 border-[#FFE500]/30' 
                  : 'bg-white/5 border border-white/10'
              }`}
              data-testid={`doccia-operatore-${docciaNum}`}
            >
              {/* Header Doccia */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-white">Doccia {docciaNum}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-lg ${
                  doccia.numero_bins >= 4 
                    ? 'bg-red-500 text-white' 
                    : doccia.numero_bins >= 3 
                      ? 'bg-amber-500 text-white' 
                      : hasBins 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-white/20 text-white/50'
                }`}>
                  {doccia.numero_bins}/4
                </span>
              </div>

              {hasBins ? (
                <>
                  {/* Totale KG */}
                  <div className="text-xs text-white/50 mb-2">
                    Totale: <span className="font-bold text-[#FFE500]">{doccia.kg_totali} kg</span>
                  </div>

                  {/* Preview Verticale Bins */}
                  <div className="bg-gradient-to-b from-blue-900/30 to-blue-950/50 rounded-lg p-2 border border-blue-500/20">
                    <div className="space-y-1">
                      {sortedBins.map((bin) => (
                        <div
                          key={bin.bin_number}
                          className="bg-gradient-to-r from-emerald-600/80 to-emerald-500/80 rounded p-2 border border-emerald-400/30 cursor-pointer hover:from-emerald-500 hover:to-emerald-400 transition-all"
                          onClick={() => handleOpenBinDetails(bin, docciaNum)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              <Box className="w-3 h-3 text-white/70" />
                              <span className="text-xs font-bold text-white">Bin {bin.bin_number}</span>
                            </div>
                            <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded text-white">
                              {bin.kg} kg
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]">
                            <span className="text-white/60">L: {bin.lotto}</span>
                            {bin.id_fornitore && (
                              <span className="text-cyan-400 font-bold">ID: {bin.id_fornitore}</span>
                            )}
                            {(bin.prodotto_sigla || bin.prodotto) && (
                              <span className="flex items-center gap-0.5 text-blue-200 font-bold">
                                <Package className="w-2.5 h-2.5" />
                                [{bin.prodotto_sigla || bin.prodotto}]
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-[9px] text-white/50">
                            {bin.fornitore?.slice(0, 12)} • {bin.data_inizio ? new Date(bin.data_inizio).toLocaleDateString('it-IT') : '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="h-20 flex items-center justify-center bg-white/5 rounded-lg border border-dashed border-white/10">
                  <span className="text-xs text-white/30">Doccia vuota</span>
                </div>
              )}

              {/* Progress bar */}
              <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    doccia.numero_bins >= 4 
                      ? 'bg-red-500' 
                      : doccia.numero_bins >= 3 
                        ? 'bg-amber-500' 
                        : 'bg-emerald-500'
                  }`}
                  style={{ width: `${(doccia.numero_bins / 4) * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Totali per Lotto */}
      {stats?.totali_per_lotto && stats.totali_per_lotto.length > 0 && (
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-4" data-testid="totali-lotto-operatore">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-emerald-500 p-1.5 rounded-lg">
              <Layers className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-sm font-bold text-white uppercase">Totali KG per Lotto</h3>
          </div>
          
          <div className="space-y-2">
            {stats.totali_per_lotto.map((lotto, index) => (
              <div 
                key={lotto.lotto}
                className="bg-white/5 border border-white/10 rounded-lg p-3"
                data-testid={`lotto-operatore-${index}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-white">L: {lotto.lotto}</span>
                    {(lotto.prodotto_sigla || lotto.prodotto) && (
                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] rounded font-bold flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        [{lotto.prodotto_sigla || lotto.prodotto}]
                      </span>
                    )}
                  </div>
                  <span className="text-xl font-bold text-emerald-400">{lotto.kg_totali} kg</span>
                </div>
                <div className="flex items-center justify-between text-[10px] text-white/50">
                  <span>{lotto.fornitore}</span>
                  <span>{lotto.num_bins} bins • Docce: {lotto.docce.join(', ')}</span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Totale */}
          <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
            <span className="text-xs text-white/50">Totale</span>
            <span className="text-lg font-bold text-[#FFE500]">
              {stats.totali_per_lotto.reduce((sum, l) => sum + l.kg_totali, 0).toFixed(2)} kg
            </span>
          </div>
        </div>
      )}

      {/* Totali per ID Fornitore */}
      {stats?.totali_per_id && stats.totali_per_id.length > 0 && (
        <div className="bg-cyan-500/10 backdrop-blur border border-cyan-500/30 rounded-xl p-4" data-testid="totali-id-operatore-section">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-cyan-500/20 rounded-lg">
              <Hash className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white uppercase">Totali per ID</h3>
              <p className="text-[10px] text-white/50">{stats.totali_per_id.length} ID - Tocca per dettagli</p>
            </div>
          </div>
          
          <div className="space-y-2">
            {stats.totali_per_id.map((item, index) => (
              <div key={index}>
                <div 
                  onClick={() => setExpandedId(expandedId === item.id_fornitore ? null : item.id_fornitore)}
                  className="flex items-center justify-between p-2 bg-white/5 rounded-lg border border-white/10 cursor-pointer active:bg-white/10"
                >
                  <div className="flex items-center gap-2">
                    {expandedId === item.id_fornitore ? (
                      <ChevronDown className="w-3 h-3 text-cyan-400" />
                    ) : (
                      <ChevronRight className="w-3 h-3 text-white/50" />
                    )}
                    <span className="px-2 py-1 bg-cyan-500/30 text-cyan-400 rounded font-mono font-bold text-sm">
                      {item.id_fornitore}
                    </span>
                    <div>
                      <p className="text-white text-xs">{item.num_bins} bins</p>
                      <p className="text-[10px] text-white/50 truncate max-w-[100px]">
                        {item.lotti.slice(0, 2).join(', ')}{item.lotti.length > 2 ? '...' : ''}
                      </p>
                    </div>
                  </div>
                  <span className="font-bold text-cyan-400 text-sm">{item.kg_totali} kg</span>
                </div>
                
                {/* Dettagli bins espansi */}
                {expandedId === item.id_fornitore && item.bins && (
                  <div className="ml-6 mt-1 space-y-1 border-l-2 border-cyan-500/30 pl-3">
                    {item.bins.map((bin, binIdx) => (
                      <div 
                        key={binIdx}
                        className="flex flex-col p-1.5 bg-white/5 rounded text-xs"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded font-bold text-[10px]">
                              Bin {bin.bin_number}
                            </span>
                            <span className="text-white/50">D{bin.doccia}</span>
                            {bin.prodotto && (
                              <span className="text-blue-400 text-[10px]">[{bin.prodotto}]</span>
                            )}
                          </div>
                          <span className="font-bold text-emerald-400">{bin.kg}kg</span>
                        </div>
                        {bin.data_inizio && (
                          <div className="text-[9px] text-yellow-400/70 mt-0.5">
                            Entrata: {new Date(bin.data_inizio).toLocaleDateString('it-IT')} {new Date(bin.data_inizio).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
            <span className="text-xs text-white/50">Totale ID</span>
            <span className="text-lg font-bold text-cyan-400">
              {stats.totali_per_id.reduce((sum, i) => sum + i.kg_totali, 0).toFixed(2)} kg
            </span>
          </div>
        </div>
      )}

      {/* Giacenza Cella */}
      {cellaStats.bins_attivi > 0 && (
        <div className="bg-[#8B5CF6]/10 backdrop-blur border border-[#8B5CF6]/30 rounded-xl p-4" data-testid="cella-operatore-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-[#8B5CF6]/20 rounded-lg">
                <Warehouse className="w-4 h-4 text-[#8B5CF6]" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white uppercase">Giacenza Cella</h3>
                <p className="text-[10px] text-white/50">Prodotti senza depurazione</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-[#8B5CF6]">{cellaStats.kg_totale.toFixed(2)} kg</p>
              <p className="text-[10px] text-white/50">{cellaStats.bins_attivi} prodotti</p>
            </div>
          </div>
          
          {/* Lista prodotti in cella */}
          <div className="space-y-2">
            {cellaStats.bins.map((bin, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded font-mono font-bold text-xs">
                    {bin.prodotto_sigla}
                  </span>
                  <div>
                    <p className="text-white font-medium text-xs">{bin.prodotto_nome}</p>
                    <p className="text-[10px] text-white/50">{bin.fornitore}</p>
                  </div>
                </div>
                <span className="font-bold text-emerald-400 text-sm">{bin.kg_attuali} kg</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Note informative */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
        <p className="text-xs text-blue-400 text-center">
          Tocca un bin per vedere i dettagli. Aggiornamento automatico ogni 30 secondi.
        </p>
      </div>

      {/* Modal Dettagli Bin */}
      {selectedBin && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeBinDetails}>
          <div 
            className="bg-[#0F172A] border border-white/20 rounded-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 p-3 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Bin {selectedBin.bin_number}</h3>
                <p className="text-xs text-white/80">Doccia {selectedBin.doccia_number} • Pos. {selectedBin.posizione || 1}</p>
              </div>
              <button 
                onClick={closeBinDetails}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-3 space-y-3 overflow-y-auto max-h-[70vh]">
              {/* Info Principali */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white/5 rounded-xl p-2 border border-white/10">
                  <div className="flex items-center gap-1 text-white/50 text-[10px] mb-1">
                    <Calendar className="w-3 h-3" />
                    Data Ingresso
                  </div>
                  <p className="text-white font-bold text-sm">{formatDate(selectedBin.data_inizio)}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-2 border border-white/10">
                  <div className="flex items-center gap-1 text-white/50 text-[10px] mb-1">
                    <Scale className="w-3 h-3" />
                    KG Iniziali
                  </div>
                  <p className="text-white font-bold text-sm">{selectedBin.kg_iniziali || selectedBin.kg} kg</p>
                </div>
                <div className="bg-white/5 rounded-xl p-2 border border-white/10">
                  <div className="flex items-center gap-1 text-white/50 text-[10px] mb-1">
                    <Scale className="w-3 h-3" />
                    KG Attuali
                  </div>
                  <p className="text-emerald-400 font-bold">{selectedBin.kg} kg</p>
                </div>
                <div className="bg-white/5 rounded-xl p-2 border border-white/10">
                  <div className="flex items-center gap-1 text-white/50 text-[10px] mb-1">
                    <Truck className="w-3 h-3" />
                    Fornitore
                  </div>
                  <p className="text-white font-bold text-xs truncate">{selectedBin.fornitore || '-'}</p>
                </div>
              </div>
              
              {/* Lotto e Prodotto */}
              <div className="flex gap-2">
                <div className="flex-1 bg-white/5 rounded-xl p-2 border border-white/10">
                  <p className="text-white/50 text-[10px] mb-1">Lotto</p>
                  <p className="text-[#FFE500] font-bold">{selectedBin.lotto || '-'}</p>
                </div>
                <div className="flex-1 bg-white/5 rounded-xl p-2 border border-white/10">
                  <p className="text-white/50 text-[10px] mb-1">ID</p>
                  <p className="text-cyan-400 font-bold">{selectedBin.id_fornitore || '-'}</p>
                </div>
                <div className="flex-1 bg-white/5 rounded-xl p-2 border border-white/10">
                  <p className="text-white/50 text-[10px] mb-1">Prodotto</p>
                  <p className="text-blue-400 font-bold">[{selectedBin.prodotto_sigla || selectedBin.prodotto || '-'}]</p>
                </div>
              </div>
              
              {/* Movimenti */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ArrowRightLeft className="w-4 h-4 text-white/50" />
                  <h4 className="text-white font-bold text-sm">Movimenti ({binMovimenti.length})</h4>
                </div>
                
                {binMovimenti.length > 0 ? (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {binMovimenti.map((mov, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-2 border border-white/10 text-xs">
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            mov.tipo_movimento === 'ingresso' ? 'bg-emerald-500/20 text-emerald-400' :
                            mov.tipo_movimento === 'uscita_parziale' ? 'bg-amber-500/20 text-amber-400' :
                            mov.tipo_movimento === 'uscita_totale' ? 'bg-red-500/20 text-red-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {mov.tipo_movimento?.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className="text-white/50 text-[10px]">{formatDate(mov.timestamp || mov.data_ora_uscita)}</span>
                        </div>
                        {mov.kg_movimento && (
                          <p className="text-white/70 text-[10px] mt-1">{mov.kg_movimento} kg</p>
                        )}
                        {mov.destinazione && (
                          <p className="text-white/50 text-[10px]">→ {mov.destinazione}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/30 text-xs text-center py-3">Nessun movimento registrato</p>
                )}
              </div>
              
              {/* Pulsanti Azioni (solo Uscita e Spostamento per operatore) */}
              {!modalAction && (
                <div className="flex gap-2 pt-3 border-t border-white/10">
                  <button
                    onClick={() => setModalAction('uscita')}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-xl text-sm font-bold hover:bg-amber-500/30 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Uscita
                  </button>
                  <button
                    onClick={() => setModalAction('spostamento')}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-xl text-sm font-bold hover:bg-purple-500/30 transition-colors"
                  >
                    <MoveRight className="w-4 h-4" />
                    Sposta
                  </button>
                  <button
                    onClick={() => {
                      setModalAction('unione');
                      loadBinsStessoLotto(selectedBin.bin_number);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded-xl text-sm font-bold hover:bg-cyan-500/30 transition-colors"
                  >
                    <ArrowRightLeft className="w-4 h-4" />
                    Unisci
                  </button>
                </div>
              )}
              
              {/* Form Uscita */}
              {modalAction === 'uscita' && (
                <form onSubmit={handleSubmitUscita} className="space-y-3 pt-3 border-t border-white/10">
                  <h4 className="text-white font-bold text-sm flex items-center gap-2">
                    <LogOut className="w-4 h-4 text-amber-400" />
                    Registra Uscita (disponibili: {selectedBin.kg} kg)
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-white/50 text-[10px]">KG Uscita *</label>
                      <input
                        type="number"
                        step="0.1"
                        max={selectedBin.kg}
                        required
                        value={uscitaData.kg_uscita}
                        onChange={(e) => setUscitaData({...uscitaData, kg_uscita: e.target.value})}
                        className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                        placeholder="Es: 10"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-[10px]">Data/Ora</label>
                      <input
                        type="datetime-local"
                        value={uscitaData.data_ora_uscita}
                        onChange={(e) => setUscitaData({...uscitaData, data_ora_uscita: e.target.value})}
                        className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-white/50 text-[10px]">Destinazione</label>
                      <input
                        type="text"
                        value={uscitaData.destinazione}
                        onChange={(e) => setUscitaData({...uscitaData, destinazione: e.target.value})}
                        className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                        placeholder="Es: Cliente XYZ"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setModalAction(null)}
                      className="flex-1 px-2 py-1.5 bg-white/10 text-white border border-white/20 rounded-lg text-sm font-bold hover:bg-white/20"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={actionLoading || !uscitaData.kg_uscita}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-bold hover:bg-amber-600 disabled:opacity-50"
                    >
                      {actionLoading ? 'Invio...' : <><LogOut className="w-3 h-3" /> Conferma</>}
                    </button>
                  </div>
                </form>
              )}
              
              {/* Form Spostamento */}
              {modalAction === 'spostamento' && (
                <form onSubmit={handleSubmitSpostamento} className="space-y-3 pt-3 border-t border-white/10">
                  <h4 className="text-white font-bold text-sm flex items-center gap-2">
                    <MoveRight className="w-4 h-4 text-purple-400" />
                    Sposta Bin
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-white/50 text-[10px]">Nuovo N. Bin *</label>
                      <input
                        type="text"
                        required
                        value={spostamentoData.nuovo_bin_number}
                        onChange={(e) => setSpostamentoData({...spostamentoData, nuovo_bin_number: e.target.value})}
                        className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                        placeholder="Es: 15"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-[10px]">Nuova Doccia *</label>
                      <select
                        required
                        value={spostamentoData.nuova_doccia_number}
                        onChange={(e) => setSpostamentoData({...spostamentoData, nuova_doccia_number: e.target.value})}
                        className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      >
                        <option value="">Seleziona...</option>
                        {[1,2,3,4,5,7,8,9,10,11,12].map(d => (
                          <option key={d} value={d}>Doccia {d}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-white/50 text-[10px]">KG da spostare</label>
                      <input
                        type="number"
                        step="0.1"
                        max={selectedBin.kg}
                        value={spostamentoData.kg_uscita}
                        onChange={(e) => setSpostamentoData({...spostamentoData, kg_uscita: e.target.value})}
                        className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-[10px]">Data/Ora</label>
                      <input
                        type="datetime-local"
                        value={spostamentoData.data_ora_uscita}
                        onChange={(e) => setSpostamentoData({...spostamentoData, data_ora_uscita: e.target.value})}
                        className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setModalAction(null)}
                      className="flex-1 px-2 py-1.5 bg-white/10 text-white border border-white/20 rounded-lg text-sm font-bold hover:bg-white/20"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      disabled={actionLoading || !spostamentoData.nuovo_bin_number || !spostamentoData.nuova_doccia_number}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-purple-500 text-white rounded-lg text-sm font-bold hover:bg-purple-600 disabled:opacity-50"
                    >
                      {actionLoading ? 'Invio...' : <><MoveRight className="w-3 h-3" /> Conferma</>}
                    </button>
                  </div>
                </form>
              )}

              {/* Form Unione */}
              {modalAction === 'unione' && (
                <form onSubmit={handleSubmitUnione} className="space-y-3 pt-3 border-t border-white/10">
                  <h4 className="text-white font-bold text-sm flex items-center gap-2">
                    <ArrowRightLeft className="w-4 h-4 text-cyan-400" />
                    Unisci in altro Bin (stesso lotto: {selectedBin?.lotto})
                  </h4>
                  
                  {binsStessoLotto.length === 0 ? (
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-amber-400 text-xs text-center">
                        Nessun altro bin attivo con lo stesso lotto.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="text-white/50 text-[10px]">Bin Destinazione *</label>
                        <select
                          value={unioneData.bin_destinazione}
                          onChange={(e) => setUnioneData(prev => ({...prev, bin_destinazione: e.target.value}))}
                          className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                          required
                        >
                          <option value="">Seleziona bin...</option>
                          {binsStessoLotto.map(b => (
                            <option key={b.bin_number} value={b.bin_number}>
                              {b.bin_number} - D{b.doccia_number} - {b.kg_attuali?.toFixed(1)}kg
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-white/50 text-[10px]">KG da Trasferire *</label>
                          <input
                            type="number"
                            step="0.1"
                            value={unioneData.kg_da_trasferire}
                            onChange={(e) => setUnioneData(prev => ({...prev, kg_da_trasferire: e.target.value}))}
                            className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                            max={selectedBin?.kg}
                            required
                          />
                          <p className="text-[10px] text-white/30">Max: {selectedBin?.kg} kg</p>
                        </div>
                        <div>
                          <label className="text-white/50 text-[10px]">Data/Ora</label>
                          <input
                            type="datetime-local"
                            value={unioneData.data_operazione}
                            onChange={(e) => setUnioneData(prev => ({...prev, data_operazione: e.target.value}))}
                            className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                            required
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setModalAction(null)}
                          className="flex-1 px-2 py-1.5 bg-white/10 text-white border border-white/20 rounded-lg text-sm font-bold hover:bg-white/20"
                        >
                          Annulla
                        </button>
                        <button
                          type="submit"
                          disabled={actionLoading || !unioneData.bin_destinazione || !unioneData.kg_da_trasferire}
                          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-cyan-500 text-white rounded-lg text-sm font-bold hover:bg-cyan-600 disabled:opacity-50"
                        >
                          {actionLoading ? 'Invio...' : <><ArrowRightLeft className="w-3 h-3" /> Conferma</>}
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatoreStatoDocce;
